import { CriticalMissingScanned } from 'components/partials/charts/CriticalMissingScannedChart';
import MarketShare from 'components/partials/charts/ShareChart';
import { swalToastError } from 'components/utilities/SwalToastUtil';
import dayjs from 'dayjs';
import SingleBarChart from 'helpers/BarChart';
import { SingleLineChart } from 'helpers/LineChart';
import { groupDataByField } from 'helpers/productionHelper';
import React, { useEffect, useState } from 'react';
import { Button, Col, Form, InputGroup } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { useSearchParams } from 'react-router-dom';
import { getRTLMissingScansByDate } from 'services/stock-location/productionServices';

const HAS_NOT_QC_SCANNED = 'has not scanned at QC';
const HAS_NOT_GLAZING_SCANNED = 'has not scanned at Glazing';
const HAS_NOT_ASTRAGALBAR_SCANNED = 'has not scanned at Astragal Bar';
const NC_CHECKING_FAILED = 'NC checking failed';
const colors = [
  'primary',
  'info',
  'gray-400',
  'success',
  'secondary',
  'warning',
  'danger'
];

export default function RTLMissingScanned() {
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [from, setFrom] = useState(dayjs().add(-3, 'day'));
  const [to, setTo] = useState(dayjs());

  useEffect(() => {
    const dateFrom = searchParams.get('from')
      ? searchParams.get('from')
      : dayjs(from).format('YYYY-MM-DD');
    const dateTo = searchParams.get('to')
      ? searchParams.get('to')
      : dayjs(to).format('YYYY-MM-DD');

    setFrom(new Date(dateFrom));
    setTo(new Date(dateTo));

    loadData(dateFrom, dateTo);
  }, []);

  const sortDataByDate = data => {
    return data.sort((a, b) => a.created.localeCompare(b.created));
  };

  const loadData = (dateFrom, dateTo) => {
    getRTLMissingScansByDate(dateFrom, dateTo)
      .then(res => {
        if (res.data) {
          setJobs(sortDataByDate(res.data));
        }
      })
      .catch(error => {
        console.log(error);
        swalToastError('Get RTL Missing Scans Failed!');
      });
  };

  const onClickSearch = () => {
    const dateFrom = dayjs(from).format('YYYY-MM-DD');
    const dateTo = dayjs(to).format('YYYY-MM-DD');

    loadData(dateFrom, dateTo);
  };

  const getLabelsByReason = data => {
    const groupedData = groupDataByField(data, 'reason');
    const sorted = Object.keys(groupedData).sort(function (a, b) {
      return groupedData[b].length - groupedData[a].length;
    });

    return sorted.slice(0, 5);
  };

  const getDataByReason = data => {
    const groupedData = groupDataByField(data, 'reason');
    const sorted = Object.keys(groupedData).sort(function (a, b) {
      return groupedData[b].length - groupedData[a].length;
    });

    const result = sorted.slice(0, 5).map(key => {
      const days = groupedData[key].filter(
        item =>
          new Date(item.created).getHours() >= 6 &&
          new Date(item.created).getHours() < 14
      ).length;

      const lates = groupedData[key].filter(
        item =>
          new Date(item.created).getHours() >= 14 &&
          new Date(item.created).getHours() < 22
      ).length;

      const nights = groupedData[key].filter(
        item =>
          new Date(item.created).getHours() < 6 ||
          new Date(item.created).getHours() >= 22
      ).length;
      return {
        days,
        lates,
        nights
      };
    });

    return result;
  };

  const transformDataByReasonAndShift = data => {
    const grouped = getDataByReason(data);

    const days = grouped?.map(item => item.days);
    const lates = grouped?.map(item => item.lates);
    const nights = grouped?.map(item => item.nights);

    return {
      days,
      lates,
      nights
    };
  };

  const getTotalDataByReason = data => {
    const groupedData = groupDataByField(data, 'reason');
    const sorted = Object.keys(groupedData).sort(function (a, b) {
      return groupedData[b].length - groupedData[a].length;
    });
    return sorted.slice(0, 5).map(i => groupedData[i].length);
  };

  const getDataByProductType = data => {
    return groupDataByField(data, 'productType');
  };

  const groupDataByShift = data => {
    return (
      data.length > 0 &&
      data.reduce((acc, item) => {
        const hour = new Date(item.created).getHours();

        if (hour >= 14 && hour < 22) {
          if (!acc['Lates']) {
            acc['Lates'] = [];
          }
          acc['Lates'].push(item);
        } else if (hour >= 6 && hour < 14) {
          if (!acc['Days']) {
            acc['Days'] = [];
          }
          acc['Days'].push(item);
        } else {
          if (!acc['Nights']) {
            acc['Nights'] = [];
          }
          acc['Nights'].push(item);
        }
        return acc;
      }, {})
    );
  };

  const transformData = (data, groupBy, isNotCountDuplicate) => {
    const grouped = groupBy(data);
    return Object.keys(grouped).map((item, index) => {
      const filtered = [];
      if (isNotCountDuplicate) {
        grouped[item].map(i => {
          const exist = filtered.find(
            r => r.headerId == i.headerId && r.frameId == i.frameId
          );

          if (!exist) {
            filtered.push(i);
          }
        });
      }
      return {
        value: isNotCountDuplicate ? filtered.length : grouped[item].length,
        name: item,
        color: colors[index]
      };
    });
  };

  const groupDataByDate = data => {
    return (
      data.length > 0 &&
      data.reduce((acc, item) => {
        if (!acc[dayjs(item.created).format('DD/MM/YYYY')]) {
          acc[dayjs(item.created).format('DD/MM/YYYY')] = [];
        }
        acc[dayjs(item.created).format('DD/MM/YYYY')].push(item);
        return acc;
      }, {})
    );
  };
  const getLabelsByDate = data => {
    const grouped = groupDataByDate(data);
    return Object.keys(grouped);
  };
  const getDataByDate = data => {
    const grouped = groupDataByDate(data);
    return Object.keys(grouped).map(i => grouped[i].length);
  };

  const getDataByDateAndMissingQC = data => {
    const grouped = groupDataByDate(data);
    return Object.keys(grouped).map(d => {
      return grouped[d].filter(v => v.reason === HAS_NOT_QC_SCANNED).length;
    });
  };
  const getDataByDateAndMissingATB = data => {
    const grouped = groupDataByDate(data);
    return Object.keys(grouped).map(d => {
      return grouped[d].filter(v => v.reason === HAS_NOT_ASTRAGALBAR_SCANNED)
        .length;
    });
  };
  const getDataByDateAndMissingGLZ = data => {
    const grouped = groupDataByDate(data);
    return Object.keys(grouped).map(d => {
      return grouped[d].filter(v => v.reason === HAS_NOT_GLAZING_SCANNED)
        .length;
    });
  };
  const getDataByDateNCCheckingFailed = data => {
    const grouped = groupDataByDate(data);
    return Object.keys(grouped).map(d => {
      return grouped[d].filter(v => v.reason === NC_CHECKING_FAILED).length;
    });
  };

  const getDataFirstDate = (data, type) => {
    const grouped = groupDataByDate(data);
    let result = 0;
    Object.keys(grouped).map((d, index) => {
      if (index == 0) {
        result = grouped[d].filter(v => v.reason === type).length;
      }
    });
    return result;
  };

  const getDataLastDate = (data, type) => {
    const grouped = groupDataByDate(data);
    let result = 0;
    Object.keys(grouped).map((d, index) => {
      if (index === Object.keys(grouped).length - 1) {
        result = grouped[d].filter(v => v.reason === type).length;
      }
    });
    return result;
  };

  const getGapfromFirstToLastDate = (data, type) => {
    let dataLastDate = getDataLastDate(data, type);
    let dataFristDate = getDataFirstDate(data, type);
    if (dataFristDate == 0 && dataLastDate == 0) {
      return 0;
    }

    if (dataFristDate == 0) {
      return 100;
    }

    return (dataLastDate / dataFristDate - 1) * 100;
  };

  const getTotal = data => {
    return data.length > 0 ? data.reduce((acc, item) => acc + +item) : 0;
  };

  return (
    <div>
      <Form.Label sm={1}>Date Range</Form.Label>
      <Col className="mx-1 d-flex gap-4 mb-4">
        <Col className="d-flex col-3">
          <Col xs={5}>
            <DatePicker
              calendarStartDay={1}
              id="date-from"
              className="form-control"
              placeholderText="Select From"
              onChange={date => setFrom(new Date(date).toISOString())}
              dateFormat={'dd/MM/yyyy'}
              selected={new Date(from)}
            />
          </Col>
          <Col xs={2}>
            <InputGroup.Text>To</InputGroup.Text>
          </Col>
          <Col xs={5}>
            <DatePicker
              calendarStartDay={1}
              id="date-to"
              className="form-control"
              placeholderText="Select To"
              onChange={date => setTo(new Date(date).toISOString())}
              dateFormat={'dd/MM/yyyy'}
              selected={new Date(to)}
            />
          </Col>
        </Col>

        <Col className="d-flex col-1">
          <Button variant="success" onClick={onClickSearch}>
            Search
          </Button>
        </Col>
      </Col>

      <div
        style={{
          display: 'flex',
          alignItems: 'start',
          justifyContent: 'start',
          flexWrap: 'wrap',
          gap: '10px'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px'
          }}
        >
          <CriticalMissingScanned
            title={'Total Frames Missing QC'}
            total={getTotal(getDataByDateAndMissingQC(jobs))}
            labels={getLabelsByDate(jobs)}
            data={getDataByDateAndMissingQC(jobs)}
            color={'#23c6c8'}
            gap={getGapfromFirstToLastDate(jobs, HAS_NOT_QC_SCANNED)}
          />
          <CriticalMissingScanned
            title={'Total Frames Missing Astragal Bar'}
            total={getTotal(getDataByDateAndMissingATB(jobs))}
            labels={getLabelsByDate(jobs)}
            data={getDataByDateAndMissingATB(jobs)}
            color={'#23c6c8'}
            gap={getGapfromFirstToLastDate(jobs, HAS_NOT_ASTRAGALBAR_SCANNED)}
          />

          <CriticalMissingScanned
            title={'Total Frames Missing Glazing'}
            total={getTotal(getDataByDateAndMissingGLZ(jobs))}
            labels={getLabelsByDate(jobs)}
            data={getDataByDateAndMissingGLZ(jobs)}
            color={'#23c6c8'}
            gap={getGapfromFirstToLastDate(jobs, HAS_NOT_GLAZING_SCANNED)}
          />
          <CriticalMissingScanned
            title={'Total Frames NC Checking Failed'}
            total={getTotal(getDataByDateNCCheckingFailed(jobs))}
            labels={getLabelsByDate(jobs)}
            data={getDataByDateNCCheckingFailed(jobs)}
            color={'#23c6c8'}
            gap={getGapfromFirstToLastDate(jobs, NC_CHECKING_FAILED)}
          />
          <MarketShare
            data={transformData(jobs, groupDataByShift, false)}
            radius={['95%', '83%']}
            title={'Shifts Missing Scans'}
          />
          <MarketShare
            data={transformData(jobs, getDataByProductType, true)}
            radius={['95%', '83%']}
            title={'Products Missing Scans'}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            width: '100%'
          }}
        >
          <SingleBarChart
            width={700}
            title={'Top 5 Areas Missing Scans'}
            labels={getLabelsByReason(jobs)}
            dataset={transformDataByReasonAndShift(jobs)}
            dataTotal={getTotalDataByReason(jobs)}
          />

          <SingleLineChart
            dataset={getDataByDate(jobs)}
            labels={getLabelsByDate(jobs)}
            borderColor={'#0275d8'}
            borderWidth={3}
            title={'Trend Of Missing Scans'}
            legendLabel="Frame"
            width={900}
          />
        </div>
      </div>
    </div>
  );
}
